import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { CalendarRange, ChevronsLeft, ChevronsRight } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

export const SALARY_PERIOD_MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

export const SALARY_PERIOD_MIN_YEAR = 1980;
export const SALARY_PERIOD_MAX_YEAR = () => new Date().getFullYear() + 1;

export type SalaryPeriodSelection = {
  year: number;
  month: number;
};

export type SalaryPeriodMonthGridProps = {
  /** Year currently shown in the year navigator. */
  browseYear: number;
  onBrowseYearChange: (year: number) => void;
  /** Selected period (month is 1–12). Null/undefined = none selected. */
  selectedYear?: number | null;
  selectedMonth?: number | null;
  onSelect: (year: number, month: number) => void;
  minYear?: number;
  maxYear?: number;
  /** When set, only these periods are clickable. */
  availablePeriods?: readonly SalaryPeriodSelection[];
  isMonthDisabled?: (year: number, month: number) => boolean;
  showSelectedFooter?: boolean;
  emptySelectedLabel?: string;
  className?: string;
};

function periodKey(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, "0")}`;
}

export function formatSalaryPeriodLabel(year: number, month: number): string {
  if (!year || !month) return "";
  return format(new Date(year, month - 1, 1), "MMMM yyyy");
}

export function SalaryPeriodMonthGrid({
  browseYear,
  onBrowseYearChange,
  selectedYear = null,
  selectedMonth = null,
  onSelect,
  minYear = SALARY_PERIOD_MIN_YEAR,
  maxYear = SALARY_PERIOD_MAX_YEAR(),
  availablePeriods,
  isMonthDisabled,
  showSelectedFooter = true,
  emptySelectedLabel = "None",
  className,
}: SalaryPeriodMonthGridProps) {
  const availableKeys = useMemo(() => {
    if (!availablePeriods) return null;
    return new Set(availablePeriods.map((p) => periodKey(p.year, p.month)));
  }, [availablePeriods]);

  const hasSelection = selectedYear != null && selectedMonth != null && selectedYear > 0 && selectedMonth > 0;
  const selectedLabel = hasSelection
    ? formatSalaryPeriodLabel(selectedYear!, selectedMonth!)
    : emptySelectedLabel;

  return (
    <div className={cn("rounded-lg border border-border/50 bg-muted/10 p-3", className)}>
      <div className="mb-3 flex items-center justify-between gap-2 border-b border-border/50 pb-2">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          disabled={browseYear <= minYear}
          onClick={() => onBrowseYearChange(Math.max(minYear, browseYear - 1))}
          aria-label="Previous year"
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm font-semibold tabular-nums text-foreground">{browseYear}</span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          disabled={browseYear >= maxYear}
          onClick={() => onBrowseYearChange(Math.min(maxYear, browseYear + 1))}
          aria-label="Next year"
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {SALARY_PERIOD_MONTH_LABELS.map((label, monthIndex) => {
          const month = monthIndex + 1;
          const key = periodKey(browseYear, month);
          const isAvailable = availableKeys == null ? true : availableKeys.has(key);
          const disabledByFn = isMonthDisabled?.(browseYear, month) === true;
          const disabled = !isAvailable || disabledByFn;
          const isSelected = selectedYear === browseYear && selectedMonth === month;
          return (
            <button
              key={label}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(browseYear, month)}
              className={cn(
                "h-10 rounded-md text-sm font-medium transition-colors",
                isSelected
                  ? "bg-primary text-primary-foreground font-semibold shadow-sm"
                  : disabled
                    ? "cursor-not-allowed border border-transparent bg-muted/30 text-muted-foreground/50"
                    : "border border-border/50 bg-background text-foreground hover:bg-muted",
              )}
            >
              {label}
            </button>
          );
        })}
      </div>
      {showSelectedFooter ? (
        <p className="mt-3 text-center text-xs text-muted-foreground">
          Selected: <span className="font-medium text-foreground">{selectedLabel}</span>
        </p>
      ) : null}
    </div>
  );
}

export type SalaryPeriodFilterFieldProps = {
  label?: string;
  selectedYear: number | null;
  selectedMonth: number | null;
  onChange: (year: number | null, month: number | null) => void;
  browseYear: number;
  onBrowseYearChange: (year: number) => void;
  clearLabel?: string;
  emptySelectedLabel?: string;
  minYear?: number;
  maxYear?: number;
  className?: string;
};

/** Inline filter control: label, optional clear, and month grid. */
export function SalaryPeriodFilterField({
  label = "Salary period",
  selectedYear,
  selectedMonth,
  onChange,
  browseYear,
  onBrowseYearChange,
  clearLabel = "All periods",
  emptySelectedLabel = "All periods",
  minYear,
  maxYear,
  className,
}: SalaryPeriodFilterFieldProps) {
  const hasSelection = selectedYear != null && selectedMonth != null;

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs text-muted-foreground">{label}</Label>
        {hasSelection ? (
          <button
            type="button"
            className="text-xs font-medium text-primary hover:underline"
            onClick={() => onChange(null, null)}
          >
            {clearLabel}
          </button>
        ) : null}
      </div>
      <SalaryPeriodMonthGrid
        browseYear={browseYear}
        onBrowseYearChange={onBrowseYearChange}
        selectedYear={selectedYear}
        selectedMonth={selectedMonth}
        onSelect={(year, month) => onChange(year, month)}
        minYear={minYear}
        maxYear={maxYear}
        emptySelectedLabel={emptySelectedLabel}
      />
    </div>
  );
}

export type SalaryPeriodMonthPickerProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  selectedYear?: number | null;
  selectedMonth?: number | null;
  onSelect: (year: number, month: number) => void;
  minYear?: number;
  maxYear?: number;
  availablePeriods?: readonly SalaryPeriodSelection[];
  isMonthDisabled?: (year: number, month: number) => boolean;
  disabled?: boolean;
  placeholder?: string;
  title?: string;
  description?: string;
  emptyMessage?: string;
  showSelectedFooter?: boolean;
  emptySelectedLabel?: string;
  closeOnSelect?: boolean;
  triggerClassName?: string;
  iconClassName?: string;
};

/** Trigger button + popover month grid (create/edit forms). */
export function SalaryPeriodMonthPicker({
  open: openProp,
  onOpenChange,
  selectedYear = null,
  selectedMonth = null,
  onSelect,
  minYear = SALARY_PERIOD_MIN_YEAR,
  maxYear = SALARY_PERIOD_MAX_YEAR(),
  availablePeriods,
  isMonthDisabled,
  disabled,
  placeholder = "Select period",
  title = "Select salary period",
  description,
  emptyMessage = "No available salary periods.",
  showSelectedFooter = true,
  emptySelectedLabel = "None",
  closeOnSelect = true,
  triggerClassName,
  iconClassName,
}: SalaryPeriodMonthPickerProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = openProp ?? uncontrolledOpen;
  const setOpen = onOpenChange ?? setUncontrolledOpen;

  const resolvedMinYear = useMemo(() => {
    if (availablePeriods && availablePeriods.length > 0) {
      return Math.min(...availablePeriods.map((p) => p.year));
    }
    return minYear;
  }, [availablePeriods, minYear]);

  const resolvedMaxYear = useMemo(() => {
    if (availablePeriods && availablePeriods.length > 0) {
      return Math.max(...availablePeriods.map((p) => p.year));
    }
    return maxYear;
  }, [availablePeriods, maxYear]);

  const [browseYear, setBrowseYear] = useState(
    () => selectedYear || new Date().getFullYear(),
  );

  useEffect(() => {
    if (!open) return;
    if (selectedYear && selectedYear > 0) {
      setBrowseYear(selectedYear);
      return;
    }
    if (availablePeriods && availablePeriods.length > 0) {
      setBrowseYear(availablePeriods[0]!.year);
      return;
    }
    setBrowseYear(new Date().getFullYear());
  }, [open, selectedYear, availablePeriods]);

  const hasSelection = selectedYear != null && selectedMonth != null && selectedYear > 0 && selectedMonth > 0;
  const selectedLabel = hasSelection
    ? formatSalaryPeriodLabel(selectedYear!, selectedMonth!)
    : null;

  const hasOptions = availablePeriods == null || availablePeriods.length > 0;

  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            "glass-input h-10 w-full justify-start text-left font-normal",
            !selectedLabel && "text-muted-foreground",
            triggerClassName,
          )}
          disabled={disabled}
        >
          <CalendarRange className={cn("mr-2 h-4 w-4 shrink-0 opacity-70", iconClassName)} />
          <span className="truncate">{selectedLabel ?? placeholder}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="z-[100] w-[min(100vw-2rem,20rem)] p-4" align="start" sideOffset={6}>
        <div className="mb-3 space-y-1">
          <p className="text-sm font-semibold text-foreground">{title}</p>
          {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
        </div>
        {!hasOptions ? (
          <p className="rounded-lg border border-border/50 bg-muted/10 p-3 text-center text-xs text-muted-foreground">
            {emptyMessage}
          </p>
        ) : (
          <SalaryPeriodMonthGrid
            browseYear={browseYear}
            onBrowseYearChange={setBrowseYear}
            selectedYear={selectedYear}
            selectedMonth={selectedMonth}
            onSelect={(year, month) => {
              onSelect(year, month);
              if (closeOnSelect) setOpen(false);
            }}
            minYear={resolvedMinYear}
            maxYear={resolvedMaxYear}
            availablePeriods={availablePeriods}
            isMonthDisabled={isMonthDisabled}
            showSelectedFooter={showSelectedFooter}
            emptySelectedLabel={emptySelectedLabel}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}
